import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { EmptyState } from "../../components/ui/empty-state";
import { getFamiliasWithPlanesCount, updateFamilia, deleteFamilia } from "../../services/familiasService";
import { Card, CardContent, CardFooter } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch, RadioPills } from "../../components/ui/switch";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { PlusCircle, Pencil, Trash2, Package } from "lucide-react";

export default function FamiliasList() {
  const [familias, setFamilias] = useState([]);
  const [filteredFamilias, setFilteredFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [familiaToDelete, setFamiliaToDelete] = useState(null);
  const [filter, setFilter] = useState("todos"); // New state for filter

  useEffect(() => {
    loadFamilias();
  }, []);

  // Apply filter whenever familias or filter changes
  useEffect(() => {
    filterFamilias();
  }, [familias, filter]);

  const loadFamilias = async () => {
    try {
      setLoading(true);
      const data = await getFamiliasWithPlanesCount();
      setFamilias(data);
    } catch (error) {
      console.error("Error loading familias:", error);
    } finally {
      setLoading(false);
    }
  };

  // New function to filter familias based on status
  const filterFamilias = () => {
    if (filter === "todos") {
      setFilteredFamilias(familias);
    } else if (filter === "activos") {
      setFilteredFamilias(familias.filter(f => f.status === 'active' && f.archived !== 1 && f.archived !== true));
    } else if (filter === "inactivos") {
      setFilteredFamilias(familias.filter(f => f.status === 'inactive' && f.archived !== 1 && f.archived !== true));
    } else if (filter === "archivados") {
      setFilteredFamilias(familias.filter(f => f.archived === 1 || f.archived === true));
    }
  };

  const handleStatusToggle = async (familia, newStatus) => {
    try {
      // Only send the status field in the payload
      await updateFamilia(familia.recordId, {
        status: newStatus ? 'active' : 'inactive'
      });
      
      // Update local state
      setFamilias(familias.map(f => 
        f.recordId === familia.recordId 
          ? { ...f, status: newStatus ? 'active' : 'inactive' } 
          : f
      ));
    } catch (error) {
      console.error("Error updating familia status:", error);
    }
  };

  // New function to handle archive toggle
  const handleArchiveToggle = async (familia, archived) => {
    try {
      await updateFamilia(familia.recordId, {
        archived: archived
      });
      
      // Update local state
      setFamilias(familias.map(f => 
        f.recordId === familia.recordId 
          ? { ...f, archived: archived } 
          : f
      ));
    } catch (error) {
      console.error("Error updating familia archive status:", error);
    }
  };

  const handleDeleteClick = (familia) => {
    setFamiliaToDelete(familia);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!familiaToDelete) return;
    
    try {
      await deleteFamilia(familiaToDelete.recordId);
      setFamilias(familias.filter(f => f.recordId !== familiaToDelete.recordId));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting familia:", error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Familias</h1>
          <p className="text-gray-600">Visualización de todas las familias disponibles</p>
        </div>
        <Link to="/admin/familias/new">
          <Button className="flex items-center gap-2 bg-[#007D6E] hover:bg-[#006358] text-white">
            <PlusCircle className="h-5 w-5" />
            Nueva Familia
          </Button>
        </Link>
      </div>

      {/* Un valor entre varios, todos a la vista: es el radio pill del Yool DS
          (`RadioPills`, variante sin punto). Antes eran cuatro `Button` con el
          activo en teal — el mismo teal del CTA "Nuevo", que por la regla del
          sistema es el único de la vista. */}
      <RadioPills
        aria-label="Filtrar familias por estado"
        value={filter}
        onChange={setFilter}
        options={[
          { value: "todos", label: "Todos" },
          { value: "activos", label: "Activos" },
          { value: "inactivos", label: "Inactivos" },
          { value: "archivados", label: "Archivados" },
        ]}
      />

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#007D6E]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFamilias.length === 0 ? (
            <div className="col-span-full">
              <EmptyState
                icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2"/><rect x="1" y="7" width="22" height="13" rx="2"/></svg>}
                title="Sin familias"
                description="No hay familias disponibles con el filtro seleccionado."
              />
            </div>
          ) : (
            filteredFamilias.map((familia) => (
              <Card 
                key={familia.recordId} 
                className={`overflow-hidden ${familia.archived ? 'opacity-70' : ''}`}
                style={{ borderTop: `4px solid ${familia.colorDestacado}` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={familia.urlImagen} 
                    alt={familia.titulo} 
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://yooldigital.com/assets/default.png";
                    }}
                  />
                  <div className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md">
                    {familia.archived ? (
                      <div className="flex items-center space-x-2 px-2">
                        <span className="text-sm font-medium text-gray-600">Archivado</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 px-2">
                        <Switch 
                          checked={familia.status === 'active'} 
                          onCheckedChange={(checked) => handleStatusToggle(familia, checked)}
                        />
                        <span className={`text-sm font-medium ${
                          familia.status === 'active' ? 'text-[var(--yool-success)]' : 'text-[var(--yool-error)]'
                        }`}>
                          {familia.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-bold">{familia.titulo}</h2>
                      <p className="text-gray-600 mt-1 line-clamp-2">{familia.descripcion}</p>
                    </div>
                    <div className="rounded-full bg-blue-100 text-blue-800 px-2 py-1 text-xs font-medium">
                      {familia.planesCount !== undefined ? familia.planesCount : (Array.isArray(familia.planes) ? familia.planes.length : 0)}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-4 pt-0 flex justify-end gap-2">
                  <Link to={`/admin/familias/${familia.recordId}`}>
                    <Button variant="outline" size="sm" className="h-9 px-3">
                      <Pencil className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                  </Link>
                  {!familia.archived && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-9 px-3 text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                      onClick={() => handleArchiveToggle(familia, true)}
                    >
                      <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 8V18C5 19.1046 5.89543 20 7 20H17C18.1046 20 19 19.1046 19 18V8M5 8H19M5 8H3M19 8H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12L14 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Archivar
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))
          )}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la familia 
              "{familiaToDelete?.titulo}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}